// LOAD-PERF (owner 2026-11-02: "load assets, shaders, everything quickly … as long as none of it happens mid-game"; "when I
// hit start run it takes 10 to 20 seconds"). Render-side warm-up, run behind a loading screen (boot, or the Start Run
// loading screen / the map pre-build in the menus) — never while the run clock is running. It never changes gameplay: the
// rehearsal drives a throwaway Sim, and the real run's Sim is created separately.
//
// Why: three.js builds a material's shader + render pipeline the first time an object is drawn. Most effect pools are
// hidden or empty until first use, many objects are created lazily (ghost copies of the hero, debris pieces, boss art),
// and on WebGPU the GPU process finishes compiling a pipeline only when a frame first draws with it — measured on VM 110:
// a 13 s first frame at Start Run on a cold shader cache, 2.2 s stalls when a guardian / the boss arrived on WebGL2.
//
//  1. rehearse(): a short scripted fight on a throwaway Sim (every technique at rank 1 and evolved, every fusion, every foe family and
//     the guardians / boss, pickups, Surge tiers, fire, prop breaks) synced into the view WITHOUT drawing, so every lazily
//     created object, material and pool exists. Chunked (yields every few ms) so the page stays responsive.
//  2. compile(): every drawable is made visible, unculled and non-empty for a moment; the scene pass is compiled with
//     PassNode.compileAsync (parallel: KHR_parallel_shader_compile on WebGL2, createRenderPipelineAsync on WebGPU), then
//     drawn once for real into the scene pass's own render target (same pipeline keys as the game, shadow pass included;
//     the canvas is not touched), everything is restored, and we wait for the GPU to finish (so lazy driver / GPU-process
//     compiles land here too).
//  Finger Beam fingertip charge / starburst (fingerCharge.ts, 2026-11-03): one InstancedMesh + one material built with the
//  view, so forceAll() below gives it an instance and compile() builds its pipeline with everything else; the rehearsal's
//  rank-1 and evolved Finger Beam shots also run its release path. Its colour wash is a uniform in the post pipeline's
//  output node (main.ts), compiled with the first frame like the rest of that node: no pipeline is built mid-run.
import * as THREE from 'three/webgpu';
import { DT, MINIBOSSES, Sim, type InputFrame } from '../core/sim';
import { WEAPONS, type EnemyKind, type WeaponId } from '../core/content';
import { SURGE_STAGE_END } from '../core/skillStats';
import { FUSIONS, FUSION_IDS } from '../core/fusions';
import { mapFoeKindsFor } from '../core/mapFoeDefs';
import type { GameView } from './view';
import { heroGlbsSettled } from './heroGltf';
import { put } from '../app/loadStats';

/** the scene pass of the post pipeline (three's PassNode; only what the warm-up touches) */
export interface ScenePassLike { renderTarget: THREE.RenderTarget; compileAsync(renderer: THREE.WebGPURenderer): Promise<void> }
export type Progress = (frac: number, msg: string) => void;

const KINDS: EnemyKind[] = ['drone', 'skitter', 'brute', 'caster', 'lancer', 'stomper', 'bomber', 'splitter', 'wisp', 'warden'];
const now = () => performance.now();
const macrotask = () => new Promise<void>((r) => setTimeout(r, 0));

/** Wait until every command submitted so far has run on the GPU (WebGPU: queue idle; WebGL2: a fence, polled). */
export async function gpuIdle(renderer: THREE.WebGPURenderer, maxMs = 30000): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const b = renderer.backend as any;
  if (b.device?.queue) { await Promise.race([b.device.queue.onSubmittedWorkDone(), new Promise((r) => setTimeout(r, maxMs))]); return; }
  const gl = b.gl as WebGL2RenderingContext | undefined; if (!gl) return;
  const s = gl.fenceSync(gl.SYNC_GPU_COMMANDS_COMPLETE, 0); if (!s) return;
  gl.flush();
  const t0 = now();
  while (gl.clientWaitSync(s, 0, 0) === gl.TIMEOUT_EXPIRED && now() - t0 < maxMs) await new Promise((r) => setTimeout(r, 4));
  gl.deleteSync(s);
}

/** Make every drawable visible, unculled and non-empty (lights untouched: toggling one changes every lit shader). Returns the undo. */
function forceAll(scene: THREE.Scene): () => void {
  const undo: (() => void)[] = [];
  scene.traverse((o) => {
    if ((o as THREE.Light).isLight) return;
    if (!o.visible) { o.visible = true; undo.push(() => { o.visible = false; }); }
    const m = o as THREE.Mesh & { count?: number };
    const g = m.geometry as (THREE.BufferGeometry & { instanceCount?: number }) | undefined;
    if (!g && !(o as THREE.Sprite).isSprite) return;
    if (g && !g.attributes.position) { if (o.visible) { o.visible = false; undo.push(() => { o.visible = true; }); } return; } // nothing to compile
    if (o.frustumCulled) { o.frustumCulled = false; undo.push(() => { o.frustumCulled = true; }); }
    if ((m as unknown as THREE.InstancedMesh).isInstancedMesh && m.count === 0) { m.count = 1; undo.push(() => { m.count = 0; }); }
    if (g && g.drawRange.count === 0) { g.drawRange.count = Infinity; undo.push(() => { g.drawRange.count = 0; }); }
    if (g && g.instanceCount === 0) { g.instanceCount = 1; undo.push(() => { g.instanceCount = 0; }); }
  });
  return () => { for (let i = undo.length - 1; i >= 0; i--) undo[i](); };
}

/** PERF PASS 3 (title-first boot): the opposite of forceAll — hide every drawable that draws nothing right now (empty
 *  instanced pools, strips with an empty draw range), so a compile sees only what the title actually shows. Returns the undo. */
function hideEmpty(scene: THREE.Scene): () => void {
  const undo: (() => void)[] = [];
  scene.traverse((o) => {
    if (!o.visible) return;
    const m = o as THREE.Mesh & { count?: number };
    const g = m.geometry as (THREE.BufferGeometry & { instanceCount?: number }) | undefined;
    if (!g) return;
    const empty = !g.attributes.position || ((m as unknown as THREE.InstancedMesh).isInstancedMesh && (m.count ?? 1) === 0) || g.drawRange.count === 0 || g.instanceCount === 0;
    if (empty) { o.visible = false; undo.push(() => { o.visible = true; }); }
  });
  return () => { for (let i = undo.length - 1; i >= 0; i--) undo[i](); };
}

export class Prewarm {
  /** maps whose world has been rehearsed + compiled in this session (keyed by map id) */
  readonly warmed = new Set<string>();
  /** ms spent per step of the last warm-up (QA / perf overlay) */
  stats: Record<string, number> = {};
  /** QA: the drawables the title compile covered (top-level name > child) */
  titleSet: string[] = [];
  /** QA: absolute performance.now() of the last warm-up's phase boundaries (long-task attribution in ?loadflow) */
  marks: [string, number][] = [];
  private mark(k: string): void { this.marks.push([k, Math.round(now())]); }

  constructor(private renderer: THREE.WebGPURenderer, private scenePass: ScenePassLike, private view: GameView) {}

  /** Wait (bounded) for the shipped hero model and (unless heroOnly) the physics / fracture libraries, so their materials
   *  exist for compile(). */
  async assets(maxMs = 15000, heroOnly = false): Promise<void> {
    const t0 = now(), v = this.view;
    while (now() - t0 < maxMs && ((v.heroModel === 'gltf' && v.gltfHero && !v.gltfHero.ready) || (!heroOnly && !v.debris.ready))) await new Promise((r) => setTimeout(r, 25));
    this.stats.assetsMs = Math.round(now() - t0);
  }

  /** PERF PASS 3 (owner: 'load as quickly as possible in the main menu'): compile and draw once only what the title and
   *  the menus show right now (the arena, sky, props, the hero and its idle effects; every empty pool hidden), so the title
   *  can appear after a fraction of the full warm-up. The full warm-up (every technique, foe, boss, debris piece) then runs
   *  behind the title — UploadTrim.deferEmpty keeps the menu frames from compiling anything meanwhile. */
  async compileTitle(onProgress?: Progress): Promise<void> {
    const t0 = now(), r = this.renderer, v = this.view;
    onProgress?.(0, 'Compiling shaders…');
    v.scene.updateMatrixWorld(true);
    let undo = hideEmpty(v.scene);
    { const names: string[] = []; v.scene.traverse((o) => { if ((o as THREE.Mesh).geometry && o.visible) { let p: THREE.Object3D = o; while (p.parent && p.parent !== v.scene) p = p.parent; if (p.visible) names.push((p.name || p.type) + (p !== o ? '>' + (o.name || o.type) : '')); } }); this.titleSet = names; } // QA: what the title compile covers
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const pipes0 = (r as any)._pipelines?.caches?.size ?? 0;
    try { await this.scenePass.compileAsync(r); } catch (e) { console.warn('[prewarm] title compileAsync', e); }
    undo();
    this.stats.titleCompileMs = Math.round(now() - t0);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.stats.titlePipes = ((r as any)._pipelines?.caches?.size ?? 0) - pipes0;
    onProgress?.(0.7, 'Compiling shaders…');
    await macrotask();
    undo = hideEmpty(v.scene);
    const prevRT = r.getRenderTarget(), prevMRT = r.getMRT();
    const t1 = now();
    try { r.setRenderTarget(this.scenePass.renderTarget); r.setMRT(null); r.render(v.scene, v.camera); }
    catch (e) { console.warn('[prewarm] title draw', e); }
    finally { r.setRenderTarget(prevRT); r.setMRT(prevMRT); undo(); }
    this.stats.titleDrawMs = Math.round(now() - t1);
    const t2 = now();
    await gpuIdle(r, 8000);
    this.stats.titleGpuMs = Math.round(now() - t2);
    this.stats.titleMs = Math.round(now() - t0); put('warm.title', this.stats.titleMs);
    onProgress?.(1, 'Finishing up…');
  }

  /** Step 2: compile + draw everything once, invisibly, and wait for the GPU. */
  async compile(onProgress?: Progress): Promise<void> {
    const t0 = now(), r = this.renderer, v = this.view;
    this.mark('compile.start');
    onProgress?.(0, 'Compiling shaders…');
    // stand-ins for every debris piece look (tree / bush variants plain + charred, rock pieces, hot seams), only for the compile
    const extra = new THREE.Group(); extra.name = 'prewarm-extra';
    for (const o of v.debris.warmSet()) extra.add(o);
    v.scene.add(extra);
    v.scene.updateMatrixWorld(true);
    let undo = forceAll(v.scene);
    try { await this.scenePass.compileAsync(r); } catch (e) { console.warn('[prewarm] compileAsync', e); }
    undo();
    this.mark('compile.asyncDone');
    this.stats.compileAsyncMs = Math.round(now() - t0);
    onProgress?.(0.6, 'Compiling shaders…');
    await macrotask();
    // the real draw (shadow pass included) into the scene pass's own target: same pipeline keys as the game, canvas untouched.
    // Re-forced after the await: the scene may have changed meanwhile (ghost pools added).
    const t1 = now();
    undo = forceAll(v.scene);
    const prevRT = r.getRenderTarget(), prevMRT = r.getMRT();
    try { r.setRenderTarget(this.scenePass.renderTarget); r.setMRT(null); r.render(v.scene, v.camera); }
    catch (e) { console.warn('[prewarm] draw', e); }
    finally { r.setRenderTarget(prevRT); r.setMRT(prevMRT); undo(); v.scene.remove(extra); extra.clear(); }
    this.stats.drawMs = Math.round(now() - t1);
    this.mark('compile.drawn');
    onProgress?.(0.8, 'Finishing up…');
    const t2 = now();
    await gpuIdle(r);
    this.stats.gpuMs = Math.round(now() - t2);
    this.mark('compile.gpuIdle');
    onProgress?.(1, 'Finishing up…');
  }

  /** Step 1: a scripted fight on a throwaway Sim, synced into the view without drawing (see the header). Yields every ~8 ms. */
  async rehearse(mapId: string, onProgress?: Progress, full = true): Promise<void> {
    const t0 = now(), v = this.view;
    this.mark('rehearse.start');
    v.ensureArt(); // PERF PASS 3: deferred from the GameView constructor (see view.ts)
    this.mark('rehearse.artBuilt');
    const sim = new Sim({ map: mapId, seed: 4242, bossTime: 1e9, maxEnemies: 220, godMode: true, autoSkills: false, meta: {} });
    const p = sim.player;
    const press = { beam: false, transform: false };
    const input = (): InputFrame => {
      const a = sim.time * 0.6, f = { moveX: Math.cos(a) * 0.5, moveZ: Math.sin(a) * 0.5, aimX: 0, aimZ: 0, manualAim: false, ascend: 0, dash: false, beam: press.beam, transform: press.transform };
      press.beam = false; press.transform = false; return f as InputFrame;
    };
    const own: EnemyKind[] = mapFoeKindsFor(mapId); // MAP FOES (owner 2026-11-02): this map's own foes are rehearsed too (never elites)
    const ring = (n: number, r: number, elite: boolean) => {
      for (const k of [...KINDS, ...own]) for (let i = 0; i < n; i++) { const a = Math.random() * 6.283; sim.spawnEnemy(k, p.x + Math.cos(a) * r, p.z + Math.sin(a) * (r * 0.8), elite && i === 0 && !own.includes(k), 1, true); }
    };
    const killAll = () => { for (const e of sim.enemies) if (e.alive) sim.damageEnemy(e, 1e9, 0xffffff, 0, 0); };
    const ids = (Object.keys(WEAPONS) as WeaponId[]).filter((id) => id !== 'surge');
    const equip = (lvl: number) => {
      sim.weapons.clear(); sim.evolved.clear();
      for (const id of ids) { sim.weapons.set(id, lvl); if (lvl >= 5) sim.evolved.add(id); }
      for (const id of sim.weapons.keys()) (sim.wTimers as Record<string, number>)[id] = 0;
      p.ki = 1e6; press.beam = true;
    };
    const surge = (tier: number) => { sim.weapons.set('surge', [0, 1, 3, 5][tier]); if (tier === 3) sim.evolved.add('surge'); else sim.evolved.delete('surge'); p.ki = SURGE_STAGE_END[tier - 1]; p.transformT = 0; press.transform = true; };
    // SURGE FORMS IV-VIII (owner 2026-11-03): every form's aura layers, hair stage, burst and ground scorch are exercised
    // here so the first real transform into VIII compiles nothing. The tier is forced on the throwaway Sim (this is the
    // render rehearsal, never the real run) and reset to III before the fight goes on.
    const form = (tier: number) => { p.surgeTier = tier; p.transformT = 30; v.handleEvents([{ t: 'transform', tier }], sim); };
    // the script (fixed steps @ 60 Hz); a map-only rehearsal (full = false) skips the techniques already warmed at boot
    const script: [number, () => void][] = full ? [
      [0, () => { equip(1); ring(2, 8, true); v.dodgeFx.preload(); }],
      // LEVEL-UP MOMENT: the hero burst, the evolution / fusion confirm bursts and the (?lupose) charge-up action binding
      [20, () => { v.levelBurst(sim, 'level', 2); v.gltfHero?.levelUpPose(0.3); }], [60, () => v.levelBurst(sim, 'evolve')], [95, () => v.levelBurst(sim, 'fusion')],
      [80, () => { killAll(); for (let k = 0; k <= 5; k++) (sim as unknown as { dropGem: (x: number, z: number, val: number, kind: number) => void }).dropGem(p.x + 1.5 + k * 0.7, p.z + 1, k <= 2 ? 3 : 0, k); (sim as unknown as { dropGem: (x: number, z: number, val: number, kind: number) => void }).dropGem(p.x + 1.5, p.z - 1.2, 60, 2); /* XP tiers: the Huge spire (a Large gem worth 40+) has its own body mesh */ }],
      [110, () => { equip(5); ring(2, 9, true); sim.passives.set('keen', 5); sim.skyCrits = 99; }], // Shardstorm (owner 2026-11-03): crits shatter shards and the banked crits call a Fallen Star inside this window
      [170, () => surge(1)], [215, () => surge(2)], [260, () => surge(3)],
      [272, () => form(4)], [279, () => form(5)], [286, () => form(6)], [293, () => form(7)], [299, () => form(8)], [312, () => { p.surgeTier = 3; }],
      [230, () => { sim.fire.ignite(p.x + 5, p.z + 3, 1.5, 6, 0); sim.fire.ignite(p.x - 6, p.z - 2, 1.5, 6, 0); }],
      [300, () => { killAll(); for (let i = 0; i < MINIBOSSES.length; i++) (sim as unknown as { spawnMiniboss: (i: number, t: number) => void }).spawnMiniboss(i, sim.time); }],
      [350, () => { killAll(); (sim.opts as { bossTime: number }).bossTime = sim.time + 0.05; }],
      [325, () => { if (sim.boss) sim.forceBossForm(sim.boss, 3); }], // BOSS FORMS (and TRAINING's forced forms): a guardian's later forms
      [380, () => { if (sim.boss) sim.forceBossForm(sim.boss, 3); }], // the final boss's forms 2 and 3 (the transformation plays)
      [400, () => { ring(1, 10, false); }],
      [440, () => killAll()],
      // SKILL FUSIONS: every fusion's bodies / beams / shots / burning ground and the fusion moment (cards, ribbons, glints).
      // Converging Volley (owner 2026-11-03) draws with the same pooled cards / ribbons / rings / flashes (no new materials); its
      // full cast (orbs, hover ring, dive, detonation column) runs inside this window (1.5 s to the detonation)
      [450, () => {
        sim.weapons.clear(); sim.evolved.clear(); sim.weapons.set('surge', 1);
        for (const id of FUSION_IDS) sim.fusion.add(id, 3);
        ring(2, 8, true);
        const f = FUSIONS.horizon; v.handleEvents([{ t: 'fusion', f: f.id, a: f.a, b: f.b, x: p.x, z: p.z }], sim);
      }],
      // ELEMENTAL FAMILIES (owner 2026-11-03): a Frostbite shatter, a Static Charge bolt, and the chilled / charged foe looks
      [452, () => {
        let k = 0; for (const e of sim.enemies) if (e.alive && k++ < 6) { e.chillT = 2; e.chillK = 0.4; e.chargeT = 2; }
        v.handleEvents([{ t: 'frostburst', x: p.x + 3, z: p.z, r: 2.4 }, { t: 'arc', pts: [p.x, 1.8, p.z, p.x + 3, 1.8, p.z + 1, p.x + 5, 1.8, p.z - 1], k: 'static' }], sim);
      }],
      // Second Wind trait (group C, 2026-11-03): its moment draws with the pooled explosion / rings only (no new materials);
      // run it once here so the path is exercised before a real run
      [480, () => v.handleEvents([{ t: 'secondWind', x: p.x, z: p.z, r: 8, left: 0 }], sim)],
      [500, () => ring(1, 9, false)],
      [570, () => { killAll(); sim.fusion.clearLive(); sim.fusion.owned.clear(); }],
    ] : [
      [0, () => { equip(5); ring(2, 8, true); }],
      [70, () => { killAll(); for (let i = 0; i < MINIBOSSES.length; i++) (sim as unknown as { spawnMiniboss: (i: number, t: number) => void }).spawnMiniboss(i, sim.time); }],
      [110, () => { sim.fire.ignite(p.x + 5, p.z + 3, 1.5, 6, 0); killAll(); (sim.opts as { bossTime: number }).bossTime = sim.time + 0.05; }],
      [130, () => { if (sim.boss) sim.forceBossForm(sim.boss, 3); }], // (TRAINING: forced boss forms)
      [160, () => killAll()],
    ];
    const END = full ? 590 : 200;
    script.sort((a, b) => a[0] - b[0]);
    let si = 0, slice = now();
    for (let step = 0; step < END; step++) {
      while (si < script.length && script[si][0] <= step) script[si++][1]();
      p.xpNext = 1e12; p.hp = p.maxHp;
      if (sim.state === 'levelup' || sim.state === 'boon') sim.state = 'playing';
      if (sim.state !== 'playing') break; // (victory after the final boss)
      sim.step(input());
      v.handleEvents(sim.events, sim);
      if (step % 3 === 2) v.sync(sim, 1, DT * 3);
      if (now() - slice > 8) { onProgress?.(step / END, full ? 'Warming up effects…' : 'Warming up the arena…'); await macrotask(); slice = now(); }
    }
    // ghost copies of the hero (Mirage Lure, Split Self, Phase Rend, dodge) finish loading asynchronously
    this.mark('rehearse.scriptDone');
    await heroGlbsSettled(); for (let i = 0; i < 3; i++) await macrotask();
    v.sync(sim, 1, DT);
    v.lvl.clear(); // LEVEL-UP MOMENT: nothing left over on the real run
    this.stats.rehearseMs = Math.round(now() - t0);
    this.mark('rehearse.end');
    onProgress?.(1, 'Warming up effects…');
  }

  /** rehearse + compile for the map currently built in the view; the caller resets the view afterwards */
  async warmMap(full: boolean, onProgress?: Progress): Promise<void> {
    const id = this.view.mapId; this.marks = [];
    await this.rehearse(id, (f, m) => onProgress?.(f * 0.45, m), full);
    await this.compile((f, m) => onProgress?.(0.45 + f * 0.55, m));
    this.warmed.add(id);
    for (const k in this.stats) put(`warm.${full ? 'full' : 'map'}.${k}`, this.stats[k]); // PERF PASS 3: window.__loadStats
  }
}
